import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [isRTL, setIsRTL] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isI18nReady, setIsI18nReady] = useState(false);
  const [i18n, setI18n] = useState(null);

  useEffect(() => {
    // Initialize i18n when component mounts
    const initializeI18n = async () => {
      try {
        const { default: i18nInstance } = await import('../i18n');
        setI18n(i18nInstance);
        setIsI18nReady(true);
        
        const currentLang = i18nInstance.language || 'en';
        setCurrentLanguage(currentLang);
        setIsRTL(currentLang === 'ar');
        
        // Update document direction and language
        document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = currentLang;
        
        // Update body class for RTL/LTR styling
        document.body.className = document.body.className.replace(/rtl|ltr/g, '');
        document.body.classList.add(currentLang === 'ar' ? 'rtl' : 'ltr');

        // Listen for language changes
        i18nInstance.on('languageChanged', (lng) => {
          setCurrentLanguage(lng);
          setIsRTL(lng === 'ar');
          
          // Update document direction and language
          document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
          document.documentElement.lang = lng;
          
          // Update body class for RTL/LTR styling
          document.body.className = document.body.className.replace(/rtl|ltr/g, '');
          document.body.classList.add(lng === 'ar' ? 'rtl' : 'ltr');
        });
      } catch (error) {
        console.error('Failed to initialize i18n:', error);
        setIsI18nReady(true); // Still set ready to prevent infinite loading
      }
    };

    initializeI18n();
  }, []);

  const changeLanguage = async (lng) => {
    if (i18n) {
      i18n.changeLanguage(lng);
      setCurrentLanguage(lng);
      setIsRTL(lng === 'ar');
    }
  };

  const t = (key, options = {}) => {
    if (!i18n) return key;
    return i18n.t(key, options);
  };

  const value = {
    currentLanguage,
    isRTL,
    changeLanguage,
    isI18nReady,
    t,
    availableLanguages: [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'ar', name: 'Arabic', nativeName: 'العربية' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
